/**
 * FAQ 4 ("Explore more" cards) — one normalization step from CMS/API payload →
 * what the UI renders. DB shape: faq row + faq_category[] with faqs nested as
 * category.faqs. Variant 4 presents a flat card list, so every active category's
 * active items are merged into a single priority-sorted list (no category tabs).
 */

export type Faq4Header = {
  title: string;
  titleColor: string;
  subtitle: string;
  subtitleColor: string;
};

export type Faq4Theme = {
  /** Accent used for the heading underline */
  accent: string;
  /** "View more / View less" link color */
  viewMore: string;
};

export type Faq4SectionBackdrop = {
  /** Solid fill when no image */
  fallbackBg: string;
  imageUrl: string;
  imageAlt: string;
};

export type Faq4Card = {
  id: number;
  question: string;
  questionColor: string;
  answer: string;
  answerColor: string;
  background: string;
  priority: number;
};

export type Faq4ViewModel = {
  header: Faq4Header;
  theme: Faq4Theme;
  section: Faq4SectionBackdrop;
  cards: Faq4Card[];
};

type RawFaq = {
  id: number;
  question?: string;
  question_text_color?: string;
  answer?: string;
  answer_text_color?: string;
  background_color?: string;
  priority?: number;
  is_active?: number | boolean;
  status?: number | boolean;
};

type RawCategory = {
  id: number;
  order_index?: number;
  status?: boolean;
  faqs?: RawFaq[];
};

const DEFAULT_HEADER: Faq4Header = {
  // No fallback heading: when the admin leaves the heading empty (or omits it),
  // the title stays empty so the heading + underline don't render.
  title: "",
  titleColor: "#171719",
  subtitle: "",
  subtitleColor: "#171719",
};

const DEFAULT_THEME: Faq4Theme = {
  accent: "#130554",
  viewMore: "#130554",
};

const DEFAULT_CARD_BG = "#FFFFFF";

const DEFAULT_SECTION_BG = "#FFFFFF";

function faqRowActive(f: RawFaq): boolean {
  if (f.is_active === 0 || f.is_active === false) return false;
  if (f.status === 0 || f.status === false) return false;
  return true;
}

/** A card is renderable only if it has an actual title or answer text (HTML stripped). */
function cardHasContent(c: Faq4Card): boolean {
  const question = c.question.replace(/<[^>]*>/g, "").trim();
  const answer = c.answer.replace(/<[^>]*>/g, "").trim();
  return question.length > 0 || answer.length > 0;
}

/**
 * Rich-text answers pasted into the CMS sometimes lead with an `<h1>`, which puts
 * a second (or third) h1 on a page that already has one. Demote to `<div>` rather
 * than unwrap: the authored inline styles carry the size, weight and margins, so
 * the card looks identical while the document keeps a single h1.
 */
function demoteH1(html: string): string {
  return html.replace(/<h1(\s[^>]*)?>/gi, "<div$1>").replace(/<\/h1\s*>/gi, "</div>");
}

function mapFaq(f: RawFaq): Faq4Card {
  return {
    id: f.id,
    question: f.question ?? "",
    questionColor: f.question_text_color?.trim() ? f.question_text_color : "#171719",
    answer: demoteH1(f.answer ?? ""),
    answerColor: f.answer_text_color?.trim() ? f.answer_text_color : "#373737",
    background: f.background_color?.trim() ? f.background_color : DEFAULT_CARD_BG,
    priority: f.priority ?? 0,
  };
}

type RawWrapper = { data?: Record<string, unknown> };

/**
 * Accepts the widget prop: `{ data: { ...section fields, categories } }` or an
 * already-unwrapped section.
 */
export function buildFaq4ViewModel(
  input: RawWrapper | Record<string, unknown> | null | undefined,
): Faq4ViewModel {
  const section =
    input && typeof input === "object" && "data" in input && (input as RawWrapper).data
      ? ((input as RawWrapper).data as Record<string, unknown>)
      : ((input as Record<string, unknown>) ?? {});

  const {
    title = DEFAULT_HEADER.title,
    title_text_color: titleColor = DEFAULT_HEADER.titleColor,
    title_1: subtitle = "",
    title_1_color: subtitleColor = DEFAULT_HEADER.subtitleColor,
    section_background_color: sectionBg = DEFAULT_SECTION_BG,
    section_background_image: bgImage = "",
    section_background_image_alt: bgImageAlt = "",
    background_color: accent = DEFAULT_THEME.accent,
    section_color: viewMore = DEFAULT_THEME.viewMore,
    categories: rawCats = [],
  } = section as {
    title?: string;
    title_text_color?: string;
    title_1?: string;
    title_1_color?: string;
    section_background_color?: string;
    section_background_image?: string;
    section_background_image_alt?: string;
    background_color?: string;
    section_color?: string;
    categories?: RawCategory[];
  };

  const cards = (rawCats as RawCategory[])
    .filter((c) => c.status !== false)
    .slice()
    .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
    .flatMap((c) => (c.faqs ?? []).filter(faqRowActive).map(mapFaq))
    .filter(cardHasContent)
    .sort((a, b) => a.priority - b.priority);

  const imageUrl = typeof bgImage === "string" ? bgImage.trim() : "";
  return {
    header: {
      title: typeof title === "string" ? title : "",
      titleColor,
      subtitle: typeof subtitle === "string" ? subtitle : "",
      subtitleColor,
    },
    theme: { accent, viewMore },
    section: {
      fallbackBg: sectionBg,
      imageUrl,
      imageAlt: (typeof bgImageAlt === "string" ? bgImageAlt : "").trim(),
    },
    cards,
  };
}
